import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CurrentUser, type RequestListRow, type Summary } from '../api';
import { Badge, Button, Card, CountUp, Spinner } from '../components/ui';
import {
  IconArrowRight,
  IconCheckCircle,
  IconClipboard,
  IconClock,
  IconLogout,
  IconServer,
  IconUsers,
} from '../components/icons';
import { canWorkItQueue, isAdmin, isApprover } from '../roles';

/**
 * The dashboard answers one question: is anything waiting on me? Counts first,
 * then the specific things behind them: no charts, because five small numbers
 * are the whole story and a chart would only dress them up.
 */
export function Dashboard({ user }: { user: CurrentUser }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const summary = useQuery({ queryKey: ['summary'], queryFn: () => api.summary(), refetchInterval: 60_000 });
  // A one-off flourish on the button that starts something new, distinct from
  // every button's ordinary press feedback, toggled in state rather than
  // tied to :active so it plays even on a quick tap.
  const [popped, setPopped] = useState(false);

  const create = useMutation({
    mutationFn: (type: 'ENROLMENT' | 'OFFBOARDING' = 'ENROLMENT') => api.createRequest(type),
    onSuccess: ({ request }) => {
      void queryClient.invalidateQueries({ queryKey: ['summary'] });
      navigate(`/requests/${request.id}/edit`);
    },
  });

  const canApprove = isApprover(user.role);
  const admin = isAdmin(user.role);
  const itQueueAccess = canWorkItQueue(user.role);
  const data = summary.data;

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
            Good day, {user.displayName.split(' ')[0]}
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {user.department ? `${user.department.name} · ` : ''}
            {user.email}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              setPopped(true);
              create.mutate('ENROLMENT');
            }}
            disabled={create.isPending}
          >
            <span
              className={popped ? 'animate-click-pop inline-flex' : 'inline-flex'}
              onAnimationEnd={() => setPopped(false)}
            >
              <IconClipboard className="h-4 w-4" />
            </span>
            {create.isPending ? 'Starting…' : 'New enrolment'}
          </Button>
          <Button variant="secondary" onClick={() => create.mutate('OFFBOARDING')} disabled={create.isPending}>
            <IconLogout className="h-4 w-4" />
            Offboarding
          </Button>
        </div>
      </div>

      {summary.isPending ? (
        <Spinner label="Loading your dashboard" />
      ) : !data ? (
        <Card className="p-6 text-sm text-slate-600 dark:text-slate-400">
          Could not load your summary. Reload the page to try again.
        </Card>
      ) : (
        <div className="space-y-8">
          {/* What is waiting on this person, in priority order */}
          {(data.needsMe.length > 0 ||
            (canApprove && (data.approval?.waiting ?? 0) > 0) ||
            (itQueueAccess && (data.it?.toPickUp ?? 0) > 0)) && (
            <section>
              <SectionTitle>Waiting on you</SectionTitle>
              <div className="stagger grid gap-4 lg:grid-cols-2">
                {data.needsMe.length > 0 && (
                  <ActionCard
                    tone="warning"
                    icon={<IconClock />}
                    title={`${data.needsMe.length} sent back for changes`}
                    body="An approver asked for something to be fixed. Reopen it, make the change and sign again."
                    to={`/requests/${data.needsMe[0]!.id}/edit`}
                    cta="Open the first one"
                    rows={data.needsMe}
                  />
                )}

                {canApprove && data.approval && data.approval.waiting > 0 && (
                  <ActionCard
                    tone="brand"
                    icon={<IconCheckCircle />}
                    title={`${data.approval.waiting} awaiting approval`}
                    body={
                      data.approval.oldestSubmittedAt
                        ? `The oldest has been waiting ${ageOf(new Date(data.approval.oldestSubmittedAt))}.`
                        : 'Shared across every approver: take whichever you can.'
                    }
                    to="/queue"
                    cta="Open the queue"
                    rows={data.approval.next ? [data.approval.next] : []}
                  />
                )}

                {itQueueAccess && data.it && data.it.toPickUp > 0 && (
                  <ActionCard
                    tone="copper"
                    icon={<IconServer />}
                    title={`${data.it.toPickUp} approved and waiting for IT`}
                    body={
                      data.it.mineInProgress > 0
                        ? `You already have ${data.it.mineInProgress} in progress.`
                        : 'Nobody has picked these up yet.'
                    }
                    to="/it-queue"
                    cta="Open the IT queue"
                    rows={data.it.next ? [data.it.next] : []}
                  />
                )}

                {admin && data.it && data.it.pendingUsers > 0 && (
                  <ActionCard
                    tone="neutral"
                    icon={<IconUsers />}
                    title={`${data.it.pendingUsers} waiting for access`}
                    body="People who have signed in but have no role yet, so they can see nothing."
                    to="/admin/users"
                    cta="Assign roles"
                    rows={[]}
                  />
                )}
              </div>
            </section>
          )}

          <section>
            <SectionTitle>Your requests</SectionTitle>
            {Object.values(data.mine).every((count) => count === 0) ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                You have not raised any requests yet.{' '}
                <button
                  type="button"
                  onClick={() => create.mutate('ENROLMENT')}
                  className="font-medium text-brand-700 underline underline-offset-4 dark:text-brand-300"
                >
                  Start one
                </button>
                .
              </p>
            ) : (
              <div className="stagger grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <Stat label="Drafts" value={data.mine.drafts} to="/requests" />
                <Stat label="Sent back" value={data.mine.changesRequested} tone="warning" to="/requests" />
                <Stat label="Awaiting approval" value={data.mine.awaitingApproval} to="/requests" />
                <Stat label="With IT" value={data.mine.withIt} to="/requests" />
                <Stat label="Completed" value={data.mine.completed} tone="success" to="/requests" />
              </div>
            )}
          </section>

          {(canApprove || itQueueAccess) && (
            <section>
              <SectionTitle>Across the company</SectionTitle>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {canApprove && data.approval && (
                  <Stat label="Awaiting approval" value={data.approval.waiting} to="/queue" />
                )}
                {itQueueAccess && data.it && (
                  <>
                    <Stat label="Waiting for IT" value={data.it.toPickUp} to="/it-queue" />
                    <Stat label="In progress" value={data.it.inProgress} to="/it-queue" />
                    <Stat label="Yours in progress" value={data.it.mineInProgress} to="/it-queue" />
                    {admin && <Stat label="No role yet" value={data.it.pendingUsers} to="/admin/users" />}
                  </>
                )}
              </div>
            </section>
          )}

          {data.needsMe.length === 0 &&
            (data.approval?.waiting ?? 0) === 0 &&
            (data.it?.toPickUp ?? 0) === 0 &&
            data.mine.drafts === 0 && (
              <Card className="p-10 text-center">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                  <IconCheckCircle />
                </span>
                <h2 className="mt-4 font-medium text-slate-900 dark:text-white">Nothing waiting on you</h2>
                <p className="mx-auto mt-1 max-w-sm text-sm text-slate-600 dark:text-slate-400">
                  Start an enrolment when someone new joins. It saves as you go, so you can gather the details
                  over time.
                </p>
              </Card>
            )}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-3 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
      {children}
    </h2>
  );
}

const STAT_TONE = {
  neutral: 'text-slate-900 dark:text-white',
  warning: 'text-copper-700 dark:text-copper-400',
  success: 'text-emerald-700 dark:text-emerald-400',
} as const;

/** A number is the whole message here, so it gets the weight and nothing competes. */
function Stat({
  label,
  value,
  tone = 'neutral',
  to,
}: {
  label: string;
  value: number;
  tone?: keyof typeof STAT_TONE;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="press rounded-xl border border-slate-200 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md hover:shadow-slate-900/5 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
    >
      <div
        className={`text-2xl font-semibold tabular-nums ${value === 0 ? 'text-slate-400 dark:text-slate-500' : STAT_TONE[tone]}`}
      >
        <CountUp value={value} />
      </div>
      <div className="mt-0.5 text-xs leading-snug text-slate-600 dark:text-slate-400">{label}</div>
    </Link>
  );
}

const ACTION_TONE = {
  warning: 'bg-copper-100 text-copper-700 dark:bg-copper-900/50 dark:text-copper-300',
  brand: 'bg-brand-100 text-brand-700 dark:bg-brand-900/60 dark:text-brand-200',
  copper: 'bg-copper-100 text-copper-700 dark:bg-copper-900/50 dark:text-copper-300',
  neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
} as const;

function ActionCard({
  tone,
  icon,
  title,
  body,
  to,
  cta,
  rows,
}: {
  tone: keyof typeof ACTION_TONE;
  icon: ReactNode;
  title: string;
  body: string;
  to: string;
  cta: string;
  rows: RequestListRow[];
}) {
  return (
    <Card interactive className="flex flex-col p-5">
      <div className="flex items-start gap-4">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${ACTION_TONE[tone]}`}>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-slate-900 dark:text-white">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{body}</p>
        </div>
      </div>

      {rows.length > 0 && (
        <ul className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 dark:border-slate-800">
          {rows.slice(0, 3).map((row) => (
            <li key={row.id} className="flex items-baseline gap-2 text-sm">
              <Link
                to={`/requests/${row.id}`}
                className="font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
              >
                {row.reference}
              </Link>
              <span className="min-w-0 truncate text-slate-600 dark:text-slate-400">
                {row.employeeName ?? 'not named yet'}
              </span>
              {row.startDate && (
                <span className="ml-auto shrink-0 text-xs text-slate-500 dark:text-slate-400">
                  starts {new Date(row.startDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Link
        to={to}
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
      >
        {cta}
        <IconArrowRight className="h-4 w-4" />
      </Link>
    </Card>
  );
}

function ageOf(since: Date): string {
  const hours = Math.floor((Date.now() - since.getTime()) / 3600_000);
  if (hours < 1) return 'less than an hour';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export { Badge };
